import Player from '@vimeo/player';

/**
 * Course topic data returned from the backend.
 * See CoursePageController#getCourseTopicDataJSON().
 */
interface CourseTopicData {
	CompletedLink: string;
	Topic: {
		TopicContents: CourseTopicContentData[];
	};
}

interface CourseTopicContentDataBase {
	ID: number;
	Title: string;
	CallbackUrl: string;
	CanAccess: boolean;
	IsViewed: boolean;
	IsCompleted: boolean;
}

interface CourseTopicVideoData extends CourseTopicContentDataBase {
	IsQuiz: false;
	IsVideo: true;
	IsLearningMaterial: false;
	ProgressUrl: string;
	VideoProgress: number;
	VideoProgressId: number | null;
	VideoCompletionPercent: number;
	VideoLink: string;
}

interface CourseTopicQuizData extends CourseTopicContentDataBase {
	IsQuiz: true;
	IsVideo: false;
	IsLearningMaterial: false;
}

interface CourseTopicLearningMaterialData extends CourseTopicContentDataBase {
	IsQuiz: false;
	IsVideo: false;
	IsLearningMaterial: true;
}

type CourseTopicContentData =
	| CourseTopicQuizData
	| CourseTopicVideoData
	| CourseTopicLearningMaterialData;

export type CourseTopicContentViewedResponse = {
	IsNowCompleted: boolean;
};

export type CourseTopicContentProgressResponse = {
	updated: boolean;
	id: number;
	percent: number;
};

const courseTopic = (initialData: CourseTopicData | undefined = undefined) => {
	return {
		course: initialData,
		video: undefined as CourseTopicVideoData | undefined,
		courseCompleted: false,
		videoCompletedForFirstTime: false,
		currentVideoProgressSeconds: 0,
		currentProgressId: null as number | null,
		progressAdvanced: false,
		savingVideoProgress: false,
		videoProgressUpdatePollPeriod: 3, // how many seconds should the video have progressed before we send a progress update

		setVideo(id: number) {
			const video = this.course?.Topic?.TopicContents.find((obj) => obj.ID === id);
			if (!video?.IsVideo) return;

			this.video = video;
			this.videoCompletedForFirstTime = false;
			// Topics can have more than one video, so don't carry the previous one's
			// "needs a reload" flag over. The playhead is seeded in initVideo().
			this.progressAdvanced = false;
		},

		async initVideo() {
			if (!this.video) return;

			const dialog = document.getElementById('video-dialog') as HTMLDialogElement;
			if (!dialog) return;
			dialog.showModal();

			// create a close event listener so we can clean up when the modal closes
			const closeListener = () => {
				this.cleanupVideo();
				dialog.removeEventListener('close', closeListener);
			};

			dialog.addEventListener('close', closeListener);

			// get vimeo url
			const url = document.getElementById('video-embed')?.dataset.vimeoUrl;
			const player = new Player('video-embed', { url });
			await player.ready();

			// The modal can be closed while the player is still loading, which clears this.video.
			if (!this.video) return;

			// Seed the playhead and progress row from what the server rendered for this video.
			this.currentVideoProgressSeconds = this.video.VideoProgress;
			this.currentProgressId = this.video.VideoProgressId;

			// Don't seek if the video is already viewed — the stored progress points
			// near the end, and re-opening a fully watched video should start from 0:00.
			if (this.currentVideoProgressSeconds > 0 && !this.video.IsViewed) {
				// Rejects if the stored playhead is past the end, e.g. the CMS swapped in a shorter video.
				player.setCurrentTime(this.currentVideoProgressSeconds).catch(() => {});
			}

			player.on('ended', () => this.videoEnded());
			player.on('timeupdate', (e) => this.videoProgress(e));
		},

		// Persists the playhead position only. The video is not marked as viewed/completed
		// until videoEnded fires (i.e. the user watches to the end), even at 99% progress.
		async videoProgress({ seconds, percent }: { percent: number; seconds: number }) {
			// timeupdate fires ~4x a second, so without an in-flight guard a single poll
			// boundary fires a burst of concurrent saves before the first one returns.
			if (!this.video || this.savingVideoProgress) return;

			// One-sided check: only save when the user has *advanced* past the last save point.
			// Backward seeks (re-watching a section) deliberately don't write — we track the
			// furthest point reached, not the literal current playhead.
			if (seconds - this.currentVideoProgressSeconds < this.videoProgressUpdatePollPeriod) {
				return;
			}

			const video = this.video;
			this.savingVideoProgress = true;

			try {
				const rsp = await fetch(video.ProgressUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						contentId: video.ID,
						progress: seconds,
						percent: percent * 100, // percent is 0-1 ranged
						progressId: this.currentProgressId,
					}),
				});

				if (!rsp.ok) return;

				const data = (await rsp.json()) as CourseTopicContentProgressResponse;
				this.currentProgressId = data.id;
				// Only worth reloading the page if the rendered watch percentage is now stale.
				if (data.percent !== video.VideoCompletionPercent) {
					this.progressAdvanced = true;
				}
			} catch {
				// Network failure (offline, aborted). Swallow it so the rejection isn't
				// reported, and fall through to the guard below.
			} finally {
				// Advance the guard even when the save failed, so we wait a full poll period
				// before trying again instead of re-posting on every timeupdate tick.
				this.currentVideoProgressSeconds = seconds;
				this.savingVideoProgress = false;
			}
		},

		async videoEnded() {
			if (!this.video) return;

			if (!this.video.IsViewed) {
				this.videoCompletedForFirstTime = true;
			}

			// fire event back to the server
			const rsp = await fetch(this.video.CallbackUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					contentId: this.video.ID,
				}),
			});

			const data = (await rsp.json()) as CourseTopicContentViewedResponse;

			if (data.IsNowCompleted) {
				// the user has now completed the course, so redirect to
				// the complete page when they close the video modal
				this.courseCompleted = true;
			}
		},

		closeVideo() {
			const dialog = document.getElementById('video-dialog') as HTMLDialogElement;
			if (!dialog) return;

			dialog.close();
		},

		// cleanupVideo() is called after the video dialog is closed, either by
		// clicking the close button, or pressing ESC
		cleanupVideo() {
			if (this.courseCompleted && this.course) {
				// this was the last content item in the course, so when the user
				// closes the video, redirect them to the 'completed' page
				window.location.href = this.course.CompletedLink;
			} else {
				this.video = undefined;

				if (this.videoCompletedForFirstTime || this.progressAdvanced) {
					// Reload so the server re-renders the progress ring — either the video
					// was completed, or the playhead advanced far enough to be saved.
					window.location.reload();
				}
			}
		},
	};
};

export default courseTopic;
